// Deal funnel (spec 09 §3.2; custom stages: spec 2026-10-16 §2). SQL mirror: public.funnel_report_at (61_gavion_deal_stages.sql).
import {
  DEFAULT_DEAL_STAGES,
  LOST_STAGE,
  isOpenStage,
  orderedStages,
  stageOrder,
  type StageDef,
} from "../deals/stageModel";
import type {
  FunnelReport,
  FunnelStageRow,
  ReportFilters,
} from "../../reports/types";
import { periodBounds } from "./period";
import { median, ratio, round } from "./stats";

export type FunnelDeal = {
  id: number;
  stage: string;
  amount: number | null;
  /** GAVION deal amount visibility (72_): hidden from the viewer (demo twin of deals_visible). */
  amount_hidden?: boolean | null;
  created_at: string;
  won_at?: string | null;
  archived_at?: string | null;
  sales_id?: number | null;
  source?: string | null;
  category?: string | null;
  company_id?: number | null;
  demo_held_at?: string | null;
};
export type StageEvent = {
  id?: number;
  deal_id: number;
  from_stage: string | null;
  to_stage: string;
  at: string;
};
export type FunnelCompany = {
  id: number;
  trade?: string | null;
  region?: string | null;
};
export type FunnelCall = {
  company_id: number | null;
  sales_id: number;
  direction: "outbound" | "inbound";
  dialed_at: string;
  outcome: string | null;
};

const DAY_MS = 86_400_000;

export const companyMatches = (
  company: FunnelCompany | undefined,
  filters: ReportFilters,
) =>
  (!filters.trade || company?.trade === filters.trade) &&
  (!filters.region || company?.region === filters.region);

export const selectFunnelDeals = (
  deals: readonly FunnelDeal[],
  companies: readonly FunnelCompany[],
  from: string,
  to: string,
  filters: ReportFilters,
): FunnelDeal[] => {
  const { start, end } = periodBounds(from, to);
  const byId = new Map(companies.map((c) => [c.id, c]));
  return deals.filter((d) => {
    if (d.archived_at) return false;
    if ((filters.mode ?? "cohort") === "cohort") {
      const t = Date.parse(d.created_at);
      if (t < start || t >= end) return false;
    }
    if (filters.sales_id != null && d.sales_id !== filters.sales_id)
      return false;
    if (filters.source && d.source !== filters.source) return false;
    if (filters.category && d.category !== filters.category) return false;
    if (
      (filters.trade || filters.region) &&
      !companyMatches(
        d.company_id != null ? byId.get(d.company_id) : undefined,
        filters,
      )
    )
      return false;
    return true;
  });
};

const byTime = (a: StageEvent, b: StageEvent) =>
  Date.parse(a.at) - Date.parse(b.at) || (a.id ?? 0) - (b.id ?? 0);

export const maxReachedOrder = (
  deal: FunnelDeal,
  history: readonly StageEvent[],
  stages: readonly StageDef[] = DEFAULT_DEAL_STAGES,
): number =>
  Math.max(
    1,
    ...history.map((e) => stageOrder(stages, e.to_stage) ?? 0),
    stageOrder(stages, deal.stage) ?? 0,
  );

/** Total days per open stage for one deal's ordered history (the open visit is measured to `now`). */
const dwellDays = (
  history: readonly StageEvent[],
  now: Date,
): Map<string, number> =>
  history.reduce((acc, e, i) => {
    if (!isOpenStage(e.to_stage)) return acc; // SQL: to_stage not in ('gagne', 'perdu')
    const end =
      i + 1 < history.length ? Date.parse(history[i + 1].at) : now.getTime();
    return new Map(acc).set(
      e.to_stage,
      (acc.get(e.to_stage) ?? 0) + (end - Date.parse(e.at)) / DAY_MS,
    );
  }, new Map<string, number>());

/** GAVION deal amount visibility (72_): `{ [key]: n }` when n rows have a hidden amount (SQL always sends the key). */
export const hiddenKey = <K extends string>(
  key: K,
  rows: readonly { amount_hidden?: boolean | null }[],
): Partial<Record<K, number>> => {
  const n = rows.filter((d) => d.amount_hidden === true).length;
  return n > 0 ? ({ [key]: n } as Record<K, number>) : {};
};

export const computeDealFunnel = ({
  deals,
  events,
  now,
  stages = DEFAULT_DEAL_STAGES,
}: {
  deals: readonly FunnelDeal[];
  events: readonly StageEvent[];
  now: Date;
  stages?: readonly StageDef[];
}): Omit<FunnelReport, "prospecting"> => {
  const ids = new Set(deals.map((d) => d.id));
  const history = new Map<number, StageEvent[]>();
  [...events]
    .filter((e) => ids.has(e.deal_id))
    .sort(byTime)
    .forEach((e) =>
      history.set(e.deal_id, [...(history.get(e.deal_id) ?? []), e]),
    );

  const reached = deals.map((d) =>
    maxReachedOrder(d, history.get(d.id) ?? [], stages),
  );
  const dwell = [...history.values()].map((h) => dwellDays(h, now));
  const reachedAt = (order: number) => reached.filter((r) => r >= order).length;

  // SQL: open stages 1…n in their configured order, gagne n + 1, perdu n + 2
  const base = orderedStages(stages).map((s, i) => {
    const stage = s.value;
    const open = isOpenStage(stage);
    const inStage = deals.filter((d) => d.stage === stage);
    const med = open
      ? median(dwell.flatMap((m) => (m.has(stage) ? [m.get(stage)!] : [])))
      : null;
    return {
      stage,
      open,
      count: inStage.length,
      amount: inStage.reduce(
        (sum, d) => sum + (d.amount_hidden ? 0 : (d.amount ?? 0)),
        0,
      ),
      // GAVION (72_): the viewer's hidden amounts, left out of the sum (key only when some are)
      ...hiddenKey("amount_hidden", inStage),
      reached: stage === LOST_STAGE ? inStage.length : reachedAt(i + 1),
      median_days: med == null ? null : round(med, 1),
    };
  });
  const rows: FunnelStageRow[] = base.map(({ open, ...row }, i) => ({
    ...row,
    conv_next:
      open && base[i + 1] ? ratio(base[i + 1].reached, row.reached, 4) : null,
  }));

  const won = deals.filter((d) => d.stage === "gagne");
  const wonSeen = won.filter((d) => !d.amount_hidden); // GAVION (72_)
  const lost = deals.filter((d) => d.stage === "perdu");
  const cycle = median(
    won
      .filter((d) => d.won_at)
      .map((d) => (Date.parse(d.won_at!) - Date.parse(d.created_at)) / DAY_MS),
  );
  return {
    deals: rows,
    win_rate: ratio(won.length, won.length + lost.length, 4),
    avg_won: wonSeen.length
      ? Math.round(
          wonSeen.reduce((s, d) => s + (d.amount ?? 0), 0) / wonSeen.length,
        )
      : null,
    ...hiddenKey("amounts_hidden", deals),
    cycle_median_days: cycle == null ? null : round(cycle, 1),
  };
};
